import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { addPhysicianGroupOptions, getPhysicianGroupOptions, editPhysicianGroupOptions, type PhysicianGroupPhysicianViewModel } from '@generated';
import Yup from '~/util/yup';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { StethoscopeIcon, Users, X } from 'lucide-react';
import { LibraryForm } from '../../shared/LibraryForm';
import { useNavigate, useParams } from 'react-router-dom';
import type { FormProps } from '~/shared/forms/Form';
import { formatPhoneNumber } from '~/util/functions';
import { getSearchQueryKey } from '~/util/http/useSearch';

const validationSchema = Yup.object().shape({
	name: Yup.string().label('Physician Group Name').nullable().required(),
	physicianIds: Yup.array().of(Yup.string()).label('Physicians').nullable()
});

export function EditPhysicianGroupForm() {
	const { id } = useParams<'id'>();
	const options = getPhysicianGroupOptions(id!);
	const { data: physicianGroup, isLoading } = useQuery(options);

	if (isLoading) {
		return <div>Loading...</div>;
	}

	return (
		<PhysicianGroupForm
			initialValues={{
				physicianGroupId: id,
				physicianIds: physicianGroup ? physicianGroup.physicians.map(x => x.id) : [],
				physicians: physicianGroup ? physicianGroup.physicians : []
			}}
			saveMutation={{
				...editPhysicianGroupOptions,
				invalidateQueryKeys: [options.queryKey]
			}}
			title={`Edit Physician Group: ${physicianGroup?.name}`}
			description={`Make changes to the physician group ${physicianGroup?.name}`}
		/>
	);
}

export function AddPhysicianGroupForm() {
	const navigate = useNavigate();
	return (
		<PhysicianGroupForm
			initialValues={{
				name: null,
				physicianIds: []
			}}
			saveMutation={{
				...addPhysicianGroupOptions,
				invalidateQueryKeys: [getSearchQueryKey('physician')]
			}}
			onSuccess={() => navigate('/settings/libraries/physiciangroups')}
			title="Add Physician Group"
			description="Add a physician group to the system"
		/>
	);
}

function PhysicianGroupForm(props: Pick<FormProps, 'initialValues' | 'saveMutation' | 'onSuccess'> & { title: string; description: string }) {
	const navigate = useNavigate();
	return (
		<LibraryForm
			{...props}
			TitleIcon={StethoscopeIcon}
			validationSchema={validationSchema}
			errorAction={{
				errorActionText: 'Return to Physician Group Library',
				errorAction: () => navigate('/settings/libraries/physiciangroups')
			}}
		>
			{formHelpers => {
				const addPhysician = (physicianId: string) => {
					const currentPhysicianIds = formHelpers.values.physicianIds || [];
					formHelpers.setFieldValue('physicianIds', [...currentPhysicianIds, physicianId]);
				};

				const removePhysician = (physicianId: string) => {
					const currentPhysicianIds = formHelpers.values.physicianIds || [];
					formHelpers.setFieldValue(
						'physicianIds',
						currentPhysicianIds.filter((id: string) => id !== physicianId)
					);
				};

				const selectedPhysicians: PhysicianGroupPhysicianViewModel[] = formHelpers.values.physicians || [];
				return (
					<div className="grid grid-cols-1 gap-6">
						<TextFormField name="name" />
						{selectedPhysicians.length === 0 ? (
							<div className="rounded-lg border-2 border-dashed border-gray-200 py-8 text-center text-gray-500">
								<Users className="mx-auto mb-2 size-8 text-gray-300" />
								<p className="text-sm">No physicians added yet</p>
								<p className="mt-1 text-xs">Search and click to add physicians to this group</p>
							</div>
						) : (
							<div className="space-y-2">
								<div className="mb-3 flex items-center justify-between">
									<span className="text-sm font-medium text-gray-700">
										{selectedPhysicians.length} {selectedPhysicians.length === 1 ? 'physician' : 'physicians'} in group
									</span>
								</div>
								{selectedPhysicians.map(physician => (
									<div
										key={physician.id}
										className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 transition-colors hover:bg-gray-100"
									>
										<div className="min-w-0 flex-1">
											<div className="text-sm font-semibold text-gray-900">{physician.name}</div>
											<div className="mt-1 flex items-center gap-4">
												<span className="text-xs text-gray-500">{formatPhoneNumber(physician.phoneNumber)}</span>
											</div>
										</div>
										<button
											type="button"
											onClick={() => removePhysician(physician.id)}
											className="ml-4 shrink-0 rounded-lg p-2 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600"
											title="Remove physician"
										>
											<X className="size-4" />
										</button>
									</div>
								))}
							</div>
						)}
					</div>
				);
			}}
		</LibraryForm>
	);
}
